<template>
	<header-block title="Review your purchase"></header-block>
	<div class="voucher-cart-page">
		<!-- Main Content -->
		<div class="cart-content">

			<!-- Purchase Summary -->
			<div class="purchase-summary-section">
				<h2 class="section-title">Purchase summary</h2>
				
				<div class="voucher-items">
					<VoucherCartItem
						v-for="(item, index) in cartItems" 
						:key="item.id"
						:logoUrl="item.availableVoucher.cardUrl"
						:merchantName="item.availableVoucher?.merchant?.name || item.merchantName || 'Unknown Merchant'"
						:amount="item.amount"
						:cashbackPercentage="item.availableVoucher?.effectiveDiscountPercentage || item.cashbackPercentage || 0"
						:formatter="formatter"
						:showDelete="false"
						:showBorder="index < cartItems.length - 1"
						@edit="openEditDrawer(item)"
						@delete="removeItem(item)"
					/>
				</div>
			</div>

			<!-- Order Summary -->
			<div class="order-summary">
				<div class="summary-card">
					<div class="summary-rows">
						<div class="summary-row">
							<span class="summary-label">Subtotal</span>
							<span class="summary-value">{{ formatter.format(subtotal()) }}</span>
						</div>
						<div class="summary-row">
							<span class="summary-label">Payment processing fee <img src="@/assets/i.png" class="info-icon" @click="showProcessingFeeInfo" /></span>
                            
							<span class="summary-value">{{ formatter.format(displayProcessingFee) }}</span>
						</div>
						<div class="summary-row total-row">
							<span class="summary-label"><strong>Total</strong></span>
							<span class="summary-value"><strong>{{ formatter.format(displayTotal) }}</strong></span>
						</div>
					</div>
					
					<div class="cashback-summary">
						<div class="cashback-earned">
							<span class="cashback-earned-label">Cashback you'll earn:</span>
							<span class="cashback-earned-amount">{{ formatter.format(totalCashback()) }}</span>
						</div>
						<div class="saved-to-spend" v-if="savedToSpendBalance > 0 && cartItems.length > 0">
							<div class="saved-to-spend-info">
								<div class="saved-text">
									<span class="saved-title">Use Saved to Spend</span>
									<span class="saved-balance">{{ formatter.format(savedToSpendBalance) }}</span>
								</div>
							</div>
							<MGToggle v-model="useSavedToSpend" />
						</div>
						
						<!-- Payment Breakdown when using Saved to Spend -->
						<Transition>
							<div class="payment-breakdown" v-if="useSavedToSpend && savedToSpendBalance > 0">
								<div class="breakdown-row">
									<span class="breakdown-label">Amount you pay today:</span>
									<span class="breakdown-value">{{ formatter.format(amountFromOwnMoney()) }}</span>
								</div>
								<div class="breakdown-row">
									<span class="breakdown-label">Remaining Saved to Spend:</span>
									<span class="breakdown-value">{{ formatter.format(remainingSavedToSpend()) }}</span>
								</div>
							</div>
						</Transition>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<div class="cart-footer">
			<div class="payment-info">
				<span class="powered-by">Powered by Stripe</span>
				<div class="payment-icons">
					<img src="@/assets/payment-methods.png" alt="Payment methods" class="payment-methods-img" />
				</div>
			</div>
						
			<MGButton v-if="cartItems.length > 0"
				:fullWidth="true"
				text="Pay by Card"
				:arrow="true"
				:stacked="true"
				:noCaps="true"
				@click="proceedToPayment"
				class="checkout-button"
			/>
		</div>

		<!-- Edit Drawer -->
		<DrawerPanel 
			:title="editingItem ? (editingItem.availableVoucher?.merchant?.name || editingItem.merchantName || 'Edit Voucher') : ''"
            :icon="editingItem ? (editingItem.availableVoucher?.cardUrl || editingItem.logoUrl || '') : ''"
			ref="editDrawer"
			:startOpened="false"
			openHeight="400px"
			closedHeight="0px"
			bottom="-20px" 
            :divider="false"
            backgroundColor="white" 
            openBackgroundColor="white"
            :blurBackground="true"
			@opened="onDrawerOpened"
			@closed="onDrawerClosed"
			class="editDrawer"
			:keyboard="isKeyboardVisible"
		>
			<VoucherEditForm 
                class="editForm"
				v-if="editingItem"
				:availableVoucher="editingItem.availableVoucher"
				:initialAmount="parseFloat(editingItem.amount)"
				@close="closeEditDrawer"
				@update="updateItemAmount"
				:formatter="formatter"
			/>
		</DrawerPanel>
	</div>

	<!-- Processing Fee Information Dialog -->
	<MGDialog 
		v-model="showProcessingFeeDialog"
		title="Processing Fee Information"
		maxWidth="400px"
		contentHeight="auto"
	>
		<template #content>
			<div class="processing-fee-info">
				<p>A small Stripe transaction processing fee is added to the purchase price. This allows us to provide you with the full cashback amount without any deductions later.</p>
			</div>
		</template>
		<template #actions>
			<MGButton
				text="Got it"
				@click="showProcessingFeeDialog = false"
			/>
		</template>
	</MGDialog>
</template>

<script>
import MGButton from './elements/MGButton.vue';
import MGToggle from './elements/MGToggle.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import DrawerPanel from './elements/DrawerPanel.vue';
import VoucherEditForm from './elements/VoucherEditForm.vue';
import VoucherCartItem from './elements/VoucherCartItem.vue';
import MGDialog from './elements/MGDialog.vue';
import Services from '../mixins/Services.js';

export default {
	name: "VoucherCartPage",
    mixins: [Services],
	components: {
		MGButton,
		MGToggle,
		HeaderBlock,
		DrawerPanel,
		VoucherEditForm,
		VoucherCartItem,
		MGDialog
	},
	data() {
		return {
			isExpressCheckoutAvailable: false,
			selectedPaymentMethod: 'card',
			useSavedToSpend: false,
			savedToSpendBalance: 0,
			processingFee: 0.25,
			cartItems: [],
			editingItem: null,
			showProcessingFeeDialog: false,
			isKeyboardVisible: false,
			formatter: {
				format: (value) => `£${value.toFixed(2)}`
			}
		}
	},
	computed: {
		faceValue() {
			if (!this.cartItems || !Array.isArray(this.cartItems)) {
				return 0;
			}
			return this.cartItems.reduce((total, item) => total + (parseFloat(item.amount) * (item.quantity || 1)), 0);
		},
		displayProcessingFee() {
			return this.subtotal() > 0 ? this.processingFee : 0;
		},
		displayTotal() {
			if (this.subtotal() === 0) {
				return 0;
			}
			return this.subtotal() + this.processingFee;
		}
	},
	methods: {
		onLoad() {
			this._getCustomerVouchers(() => {
				this.savedToSpendBalance = this.vouchers.currentSavings;
				this.loadCartFromSession();
			});
		},

		subtotal() {
			if (!this.cartItems || !Array.isArray(this.cartItems)) {
				return 0;
			}
			const voucherTotal = this.cartItems.reduce((total, item) => total + (parseFloat(item.amount) * (item.quantity || 1)), 0);
			
			if (this.useSavedToSpend && this.savedToSpendBalance > 0) {
				const savedToSpendUsed = Math.min(this.savedToSpendBalance, voucherTotal);
				return Math.max(0, voucherTotal - savedToSpendUsed);
			}
			
			return voucherTotal;
		},
		total() {
			return this.subtotal() + this.processingFee;
		},
		totalCashback() {
			if (!this.cartItems || !Array.isArray(this.cartItems)) {
				return 0;
			}
			return this.cartItems.reduce((total, item) => {
				const amount = parseFloat(item.amount);
				const cashbackPercentage = item.availableVoucher?.effectiveDiscountPercentage || 0;
				const cashbackAmount = (amount * cashbackPercentage) / 100;
				return total + (cashbackAmount * (item.quantity || 1));
			}, 0);
		},
		amountFromOwnMoney() {
			return this.subtotal() + this.displayProcessingFee;
		},
		remainingSavedToSpend() {
			if (!this.useSavedToSpend) {
				return this.savedToSpendBalance;
			}
			
			const voucherTotal = this.cartItems.reduce((total, item) => total + (parseFloat(item.amount) * (item.quantity || 1)), 0);
			const savedToSpendUsed = Math.min(this.savedToSpendBalance, voucherTotal);
			return Math.max(0, this.savedToSpendBalance - savedToSpendUsed);
		},
		removeItem(item) {
			if (!this.cartItems || !Array.isArray(this.cartItems)) {
				return;
			}
			this.cartItems = this.cartItems.filter(i => i.availableVoucher.id !== item.availableVoucher.id);
			this._createCart(this.cartItems);
		},
		proceedToPayment() {
			if (!this.cartItems || !Array.isArray(this.cartItems) || this.cartItems.length === 0) {
				console.error('No items in cart to proceed with payment');
				return;
			}
			console.log('Proceeding to payment...', {
				paymentMethod: this.selectedPaymentMethod,
				subTotal: this.subtotal(),
				processingFee: this.processingFee,
				total: this.total(),
				useSavedToSpend: this.useSavedToSpend,
				items: this.cartItems
			});
			var firstVoucherId = this.cartItems[0].availableVoucher.id;
			console.log('Navigating to:', { path: '/vouchers/' + firstVoucherId, query: { total: this.total(), subTotal: this.subtotal(), processingFee: this.processingFee, useSavedToSpend: this.useSavedToSpend } });
			this.$router.push({ path: '/vouchers/' + firstVoucherId, query: 
				{ 
					pay: 'true', 
					total: this.total(), 
					subTotal: this.subtotal(), 
					processingFee: this.processingFee, 
					useSavedToSpend: this.useSavedToSpend,
					amountToPayToday: this.amountFromOwnMoney(),
					faceValue: this.faceValue
				} 
			});
		},
		loadCartFromSession() {
			this._getCart((r) => {
				this.cartItems = r;
			});
		},
		openEditDrawer(item) {
			if (item && (item.availableVoucher || item.merchantName)) {
				this.editingItem = item;
				this.$refs.editDrawer.openDrawer();
				setTimeout(() => {
					this.isKeyboardVisible = true;
				}, 300);
			}
		},
		closeEditDrawer() {
			this.$refs.editDrawer.closeDrawer();
			this.isKeyboardVisible = false;
		},
		updateItemAmount(data) {
			if (!this.cartItems || !Array.isArray(this.cartItems)) {
				this.closeEditDrawer();
				return;
			}
			const item = this.cartItems.find(item => item.availableVoucher.id === this.editingItem.availableVoucher.id);
			if (item) {
				item.amount = data.amount.toString(); 
				this._createCart(this.cartItems);
			}
			this.closeEditDrawer();
		},
		onDrawerOpened() {
			// Drawer opened
		},
		onDrawerClosed() {
			this.isKeyboardVisible = false;
			this.editingItem = null;
		},
		continueShopping() {
			this.$router.push('/ShopVouchers');
		},
		showProcessingFeeInfo() {
			this.showProcessingFeeDialog = true;
		}
	},
	mounted() {
		this.onLoad();
	}
}
</script>

<style scoped>
.editForm {
    margin-top: -30px;
	margin-bottom: -40px;
}
.voucher-cart-page {
	/* min-height: 100vh; */
	display: flex;
	flex-direction: column;
}

/* Main Content */
.cart-content {
	flex: 1;
	overflow-y: auto;
}

.payment-method-section,
.purchase-summary-section {
	padding: 20px 0px 16px;
}

.section-title {
	font-weight: bold;
	font-size: 20px;
	margin: -16px 0 16px 0;
}

/* Payment Method Tabs */
.payment-tabs {
	display: flex;
	gap: 12px;
	margin-bottom: 16px;
}

.payment-tab {
	flex: 1;
	padding: 12px 20px 13px;
	border: 1px solid rgba(51, 40, 71, 0.1);
	border-radius: 4px;
	background: transparent;
	font-weight: 500;
	font-size: 13px;
	color: var(--text-color);
	cursor: pointer;
	transition: all 0.2s ease;
}

.payment-tab.active {
	background: var(--text-color);
	color: white;
	border-color: var(--text-color);
}

.payment-description {
	font-size: 11px;
	color: var(--text-color);
	margin: 0;
}

/* Voucher Items */
.voucher-items {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.quantity-controls {
	display: flex;
	align-items: center;
	gap: 0;
}

.quantity-btn {
	width: 24px !important;
	height: 24px !important;
	border-radius: 50% !important;
	background: rgba(245, 244, 246, 0.5) !important;
	color: var(--text-color) !important;
}

.quantity {
	font-size: 15px;
	color: var(--text-color);
	min-width: 40px;
	text-align: center;
}

/* Order Summary */
.order-summary {
	padding: 0 0px 20px;
}

.summary-card {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.summary-rows {
	padding: 12px 16px;
}

.summary-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.summary-row:last-child {
	margin-bottom: 0;
}

.summary-row.total-row {
	padding-top: 8px;
	border-top: 1px solid #f0f0f0;
	margin-top: 8px;
}

.summary-label {
	font-size: 13px;
	color: var(--text-color);
}

.total-row .summary-label,
.total-row .summary-value {
	font-weight: 500;
	font-size: 15px;
}

.summary-value {
	font-size: 13px;
	color: var(--text-color);
}

.info-button {
	color: var(--text-color) !important;
}
.info-icon {
    width: 14px;
    height: 14px;
    cursor: pointer;
    margin-left: 4px;
}

.cashback-summary {
	padding: 0 16px 12px;
}

.cashback-earned {
	background: #E0EBE3;
	border-radius: 60px;
	padding: 8px 16px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
}

.cashback-earned-label,
.cashback-earned-amount {
	font-weight: bold;
	font-size: 13px;
	color: var(--text-color);
}

.saved-to-spend {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.saved-to-spend-info {
	flex: 1;
}

.saved-text {
	display: flex;
	flex-direction: column;
}

.saved-title {
	font-size: 15px;
	color: var(--text-color);
}

.saved-balance {
	font-weight: bold;
	font-size: 15px;
	color: var(--text-color);
}


/* Payment Breakdown */
.payment-breakdown {
	margin-top: 16px;
	padding: 12px 16px;
	background: #F8F9FA;
	border-radius: 8px;
	border: 1px solid #E9ECEF;
}

.breakdown-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
}

.breakdown-row:last-child {
	margin-bottom: 0;
}

.breakdown-label {
	font-size: 13px;
	color: var(--text-color);
}

.breakdown-value {
	font-size: 13px;
	font-weight: 500;
	color: var(--text-color);
}

/* Footer */
.cart-footer {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	background: white;
	border-top: 1px solid rgba(51, 40, 71, 0.05);
	box-shadow: 0px -4px 8px 0px rgba(51, 40, 71, 0.05);
	padding: 24px 20px 32px;
}

.payment-info {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
	padding: 0 8px 0 4px;
}

.powered-by {
	font-size: 13px;
	color: var(--text-color);
	width: 120px;
}

.payment-methods-img {
	height: 16px;
}

.checkout-button {
	border-radius: 100px !important;
	height: 56px !important;
}

.continue-shopping-button {
	text-align: center;
	font-size: 15px;
	font-weight: bold;
	margin-top: 12px;
}

.processing-fee-info {
	text-align: center;
	line-height: 1.5;
}

.processing-fee-info p {
	margin: 0;
	font-size: 14px;
	color: var(--text-color);
}
</style>